import type { ConversationMessageModel, ConversationModel } from "#features/conversations/types/ConversationMessageModel.ts";
import { type AiApiClient, useAiApiClient } from "#lib/AiApiClient.ts";
import { AiQueryKeys } from "#lib/AiQueryKeys.ts";
import type { ConnectionRecord } from "#types/ConnectionRecord.ts";
import type { QuestionRecord } from "#types/InteractiveQuestion.ts";
import type { ToolApprovalRecord } from "#types/ToolApproval.ts";
import type { Conversation, ConversationPendingToolCall, ConversationReplayEvent } from "#types/types.ts";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";

// The BFF has no assistantStatus of its own; a message loaded from history is never mid-turn, so
// "idle" is the only value a reload could mean (and what the BFF used to hardcode here before the
// field moved to the client).
export const withLoadedAssistantStatus = (message: Conversation["messages"][number]): ConversationMessageModel => ({
    ...message,
    assistantStatus: "idle"
});

// The About People card, the prepared action's state, the approvals, the question and the connection request come back
// beside the messages, not on one. Each goes back on the message the stream showed it on, in stream order, so a turn's last card wins as it
// did live. A kind this client does not know yet is skipped.
export const withReplayedCards = (messages: ConversationMessageModel[], replayEvents: ConversationReplayEvent[], pendingToolCalls: ConversationPendingToolCall[] = []) => {
    const cards = new Map(replayEvents.flatMap(({ kind, messageId, personProfile }) =>
        (kind === "person_profile" && personProfile ? [[messageId, personProfile] as const] : [])));
    const preparedActions = new Map(replayEvents.flatMap(({ kind, messageId, preparedAction }) =>
        (kind === "prepared_action" && preparedAction ? [[messageId, preparedAction] as const] : [])));
    const toolApprovals = groupToolApprovals(messages, replayEvents, pendingToolCalls);
    const questions = new Map(replayEvents.flatMap(({ kind, messageId, question }) =>
        (kind === "question" && question ? [[messageId, question] as const] : [])));
    const connections = new Map(replayEvents.flatMap(({ kind, messageId, connection }) =>
        (kind === "integration_connection" && connection ? [[messageId, connection] as const] : [])));

    return messages.map(message => {
        const personProfile = cards.get(message.id);
        const preparedAction = preparedActions.get(message.id);
        const approvals = toolApprovals.get(message.id);
        const question = questions.get(message.id);
        const connection = connections.get(message.id);

        return {
            ...message,
            ...(personProfile && { personProfile }),
            ...(preparedAction && { preparedAction }),
            ...(approvals && { toolApprovals: approvals }),
            // The generated status is a plain string. One this client does not know shows neither the card nor the answers.
            ...(question && { question: question as QuestionRecord }),
            // Open or closed, so an older card still shows its integrations. A status this client does not know offers neither Dismiss nor a resume.
            ...(connection && { connection: connection as ConnectionRecord })
        };
    });
};

// A pending chat call comes back in both pendingToolCalls and its tool_approval event; the event is its one record. A
// drafted action's pending call has no event, so its record goes on the card's message. A BFF whose client predates
// tool_approval passes the event through without its payload, and it is skipped.
const groupToolApprovals = (messages: ConversationMessageModel[], replayEvents: ConversationReplayEvent[], pendingToolCalls: ConversationPendingToolCall[]) => {
    const byMessage = new Map<string, ToolApprovalRecord[]>();
    const add = (messageId: string, record: ToolApprovalRecord) => byMessage.set(messageId, [...byMessage.get(messageId) ?? [], record]);
    replayEvents.forEach(({ kind, messageId, toolApproval, preparedAction }) => {
        if (kind === "tool_approval" && toolApproval?.calls?.length) {
            add(messageId, toolApproval);
        }
        const draftCall = preparedAction?.status === "pending" && pendingToolCalls.find(({ toolCallId }) => toolCallId === preparedAction.toolCallId);
        if (kind === "prepared_action" && draftCall) {
            const requestedAt = messages.find(({ id }) => id === messageId)?.timestamp ?? "";
            add(messageId, { status: "pending", requestedAt, calls: [{ ...draftCall, decision: null }] });
        }
    });

    return byMessage;
};

// The live prompt's rejected edits and core's reasons, which a read does not carry, stay on its calls.
const keepLivePrompt = (previous: ConversationModel | undefined, record: ToolApprovalRecord): ToolApprovalRecord => {
    const liveRecords = previous?.messages.flatMap(({ toolApprovals }) => toolApprovals ?? []).filter(({ status }) => status === "pending") ?? [];
    const live = liveRecords.find(({ calls }) => calls.some(({ toolCallId }) => record.calls.some(call => call.toolCallId === toolCallId)));
    if (record.status !== "pending" || !live) {
        return record;
    }

    return {
        ...record,
        calls: record.calls.map(call => {
            const { editedArgs, editErrors } = live.calls.find(({ toolCallId }) => toolCallId === call.toolCallId) ?? {};

            return { ...call, ...(editedArgs && { editedArgs }), ...(editErrors && { editErrors }) };
        })
    };
};

// What only the stream gives a message. A read has none of it, so a refetch keeps it on the messages the cache holds.
const keepStreamState = (previous: ConversationModel | undefined, next: ConversationModel): ConversationModel => {
    const previousById = new Map(previous?.messages.map(message => [message.id, message]));

    return {
        ...next,
        messages: next.messages.map(message => {
            const held = previousById.get(message.id);
            const withLivePrompt = message.toolApprovals ? { ...message, toolApprovals: message.toolApprovals.map(record => keepLivePrompt(previous, record)) } : message;

            return held
                ? {
                    ...withLivePrompt,
                    ...(held.thinkingSteps && { thinkingSteps: held.thinkingSteps }),
                    ...(held.promptFeature && { promptFeature: held.promptFeature }),
                    ...(held.pageActions && { pageActions: held.pageActions }),
                    ...(held.pageActionResponse && { pageActionResponse: held.pageActionResponse })
                }
                : withLivePrompt;
        })
    };
};

export const getConversationQueryOptions = (conversationId: string, client: AiApiClient) => {
    return queryOptions({
        queryKey: AiQueryKeys.GetConversation(conversationId),
        staleTime: Infinity,
        // Keep the cache entry alive even while unobserved. On the home page a conversation can be
        // created eagerly at file-attach time (setQueryData in useCreateConversationMutation) with no
        // ConversationView mounted to observe it. Since core-python only persists the conversation on
        // the first message, the cache is the sole client-side record until then; letting the default
        // 5-minute gcTime evict it would make a later submit fetch getConversation(id) and hit a 404.
        gcTime: Infinity,
        queryFn: async({ client: queryClient, queryKey }): Promise<ConversationModel> => {
            const data = await client.getConversation(conversationId);

            return keepStreamState(
                queryClient.getQueryData<ConversationModel>(queryKey),
                { ...data, messages: withReplayedCards(data.messages.map(withLoadedAssistantStatus), data.replayEvents ?? [], data.pendingToolCalls ?? []) }
            );
        }
    });
};

export const useGetConversationSuspenseQuery = (conversationId: string) => {
    const client = useAiApiClient();

    return useSuspenseQuery(getConversationQueryOptions(conversationId, client));
};
